import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  GPXData,
  Segment,
  RaceSettings,
  PacingResult,
  NutritionProduct,
  SegmentNutritionPlan,
  CalibrationAnswers,
  CheckpointType,
} from '../types'
import { AVAILABLE_MODELS, getModelById } from '../utils/pacingModels'
import { applyScenario } from '../utils/scenarioEngine'
import { addMinutes } from 'date-fns'
import {
  calculateSegmentStats,
  findClosestPointIndex,
} from '../utils/gpxParser'
import { SAMPLE_GPX_DATA } from '../utils/sampleGpxData'
import {
  getDefaultCalibrationAnswers,
  deriveAthleteParams,
} from '../utils/calibrationEngine'
import {
  getClimateCondition,
  calculateClimateImpact,
} from '../utils/climateEngine'
import { fetchWeatherForecast, WeatherForecastData } from '../utils/weatherApi'
import { DEFAULT_PRODUCTS } from '../utils/defaultProducts'

// --- Waypoint-to-Segment helpers ---

function inferCheckpointType(
  name: string,
  desc?: string,
  type?: string,
): CheckpointType {
  const text = `${name} ${desc || ''} ${type || ''}`.toLowerCase()

  // Base de Vie / Base Camp
  if (
    /\b(base\s*de\s*vie|base\s*vida|base\s*camp|base\s*life|\bbv\b)\b/.test(
      text,
    )
  ) {
    return 'BV'
  }
  // Water point
  if (
    /\b(water|água|agua|eau|fontaine|fonte|water\s*point|point\s*d'eau)\b/.test(
      text,
    )
  ) {
    return 'Water'
  }
  // Aid station
  if (
    /\b(aid|abastecimento|ravitaillement|ravito|refreshment|food|refueling|poste|posto|checkpoint)\b/.test(
      text,
    )
  ) {
    return 'Aid'
  }
  // If it has a name, default to Aid (most GPX waypoints in races are aid stations)
  return 'Aid'
}

function buildSegmentsFromWaypoints(data: GPXData): Segment[] {
  const { points, waypoints } = data
  if (!waypoints || waypoints.length === 0) return []

  const defaultStops: Record<CheckpointType, number> = {
    None: 0,
    Water: 5,
    Aid: 10,
    BV: 20,
    Other: 10,
  }

  // Map each waypoint to its closest track point index
  const mapped = waypoints.map((wpt) => ({
    ...wpt,
    pointIndex: findClosestPointIndex(points, wpt.lat, wpt.lon),
    distFromStart: 0,
  }))

  // Assign distFromStart for each mapped waypoint
  for (const m of mapped) {
    m.distFromStart = points[m.pointIndex]?.distFromStart || 0
  }

  // Sort by position along the track
  mapped.sort((a, b) => a.pointIndex - b.pointIndex)

  // Deduplicate waypoints that are very close together (< 0.3 km)
  const deduped = [mapped[0]]
  for (let i = 1; i < mapped.length; i++) {
    const prev = deduped[deduped.length - 1]
    if (Math.abs(mapped[i].distFromStart - prev.distFromStart) >= 0.3) {
      deduped.push(mapped[i])
    }
  }

  // Filter out waypoints too close to start (< 0.5 km) or end (< 0.5 km from finish)
  const filtered = deduped.filter((wpt) => {
    return (
      wpt.distFromStart >= 0.5 && wpt.distFromStart <= data.totalDistance - 0.5
    )
  })

  if (filtered.length === 0) {
    // No valid waypoints after filtering, fall back to single segment
    const stats = calculateSegmentStats(points, 0, points.length - 1)
    return [
      {
        id: crypto.randomUUID(),
        name: 'Start to Finish',
        startPointIndex: 0,
        endPointIndex: points.length - 1,
        distance: data.totalDistance,
        elevationGain: data.totalElevationGain,
        elevationLoss: data.totalElevationLoss,
        avgGrade: stats.avgGrade,
        checkpointType: 'None',
        terrainType: 'SmoothTrail',
        stopTime: 0,
      },
    ]
  }

  // Build segments: Start → WPT1 → WPT2 → ... → Finish
  const segments: Segment[] = []
  const splitPoints = [
    { pointIndex: 0, name: 'Start', checkpointType: 'None' as CheckpointType },
    ...filtered.map((wpt) => ({
      pointIndex: wpt.pointIndex,
      name: wpt.name,
      checkpointType: inferCheckpointType(wpt.name, wpt.desc, wpt.type),
    })),
    {
      pointIndex: points.length - 1,
      name: 'Finish',
      checkpointType: 'None' as CheckpointType,
    },
  ]

  for (let i = 0; i < splitPoints.length - 1; i++) {
    const startIdx = splitPoints[i].pointIndex
    const endIdx = splitPoints[i + 1].pointIndex
    const cpType = splitPoints[i].checkpointType
    const stats = calculateSegmentStats(points, startIdx, endIdx)

    // Segment name: use the name of the START waypoint (where you arrive)
    // For the first segment, name it after the destination (first waypoint)
    const segmentName = i === 0 ? splitPoints[i + 1].name : splitPoints[i].name

    segments.push({
      id: crypto.randomUUID(),
      name: segmentName,
      startPointIndex: startIdx,
      endPointIndex: endIdx,
      distance: stats.distance,
      elevationGain: stats.elevationGain,
      elevationLoss: stats.elevationLoss,
      avgGrade: stats.avgGrade,
      checkpointType: cpType,
      terrainType: 'SmoothTrail',
      stopTime: defaultStops[cpType] || 0,
    })
  }

  return segments
}

// --- End Waypoint helpers ---

interface RaceState {
  gpxData: GPXData | null
  segments: Segment[]
  settings: RaceSettings
  pacingResults: PacingResult[]
  weatherForecast: WeatherForecastData | null

  // Nutrition State
  products: NutritionProduct[]
  nutritionPlans: Record<string, SegmentNutritionPlan>

  // Actions
  setGpxData: (data: GPXData) => void
  loadSampleData: () => void
  addSplitAtKm: (km: number, splitName?: string) => void
  updateSegment: (id: string, updates: Partial<Segment>) => void
  removeSegment: (id: string) => void
  updateSettings: (updates: Partial<RaceSettings>) => void
  updateAthleteCalibration: (answers: CalibrationAnswers) => void
  updateNutritionBaselines: (
    updates: Partial<{
      baselineChoPerHourG: number
      baselineCalPerHourKcal: number
      baselineFluidMlPerHour: number
      sodiumMultiplier: number
    }>,
  ) => void
  updateSegmentPace: (segmentId: string, pace: number) => void
  recalculatePacing: () => void
  refreshWeather: () => Promise<void>

  // Nutrition Actions
  addProduct: (product: NutritionProduct) => void
  updateProduct: (id: string, updates: Partial<NutritionProduct>) => void
  removeProduct: (id: string) => void
  updateNutritionPlan: (
    segmentId: string,
    plan: Partial<SegmentNutritionPlan>,
  ) => void
  exportProducts: () => string
  importProducts: (
    json: string,
  ) => { success: boolean; message: string; count: number }

  // Import/Export
  exportRacePlan: () => string
  importRacePlan: (json: string) => { success: boolean; message: string }
}

const DEFAULT_SETTINGS: RaceSettings = {
  raceName: 'My Trail Race',
  startTime: new Date().toISOString(),
  sunriseTime: '06:30',
  sunsetTime: '20:30',
  activeModelId: 'terrain-v1', // Default to Terrain-Adjusted

  athleteProfile: {
    answers: getDefaultCalibrationAnswers(),
    params: deriveAthleteParams(getDefaultCalibrationAnswers()),
    isCalibrated: false, // Default to false
  },

  executionScenario: 'standard',
  safePenaltyIntensity: 'default',
  defaultStopTimes: {
    None: 0,
    Water: 5,
    Aid: 10,
    BV: 20,
    Other: 10,
  },
}

export const useRaceStore = create<RaceState>()(
  persist(
    (set, get) => ({
      gpxData: null,
      segments: [],
      settings: DEFAULT_SETTINGS,
      pacingResults: [],
      weatherForecast: null,
      products: DEFAULT_PRODUCTS,
      nutritionPlans: {},

      setGpxData: (data) => {
        let initialSegments: Segment[]

        if (data.waypoints && data.waypoints.length >= 1) {
          initialSegments = buildSegmentsFromWaypoints(data)
        } else {
          const stats = calculateSegmentStats(
            data.points,
            0,
            data.points.length - 1,
          )
          initialSegments = [
            {
              id: crypto.randomUUID(),
              name: 'Start to Finish',
              startPointIndex: 0,
              endPointIndex: data.points.length - 1,
              distance: data.totalDistance,
              elevationGain: data.totalElevationGain,
              elevationLoss: data.totalElevationLoss,
              avgGrade: stats.avgGrade,
              checkpointType: 'None',
              terrainType: 'SmoothTrail',
              stopTime: 0,
            },
          ]
        }

        set((state) => ({
          gpxData: data,
          segments: initialSegments,
          settings: {
            ...DEFAULT_SETTINGS,
            raceName: data.name || state.settings.raceName,
            athleteProfile: {
              ...DEFAULT_SETTINGS.athleteProfile,
              isCalibrated: false,
            },
          },
          pacingResults: [],
        }))
        get().refreshWeather()
      },

      loadSampleData: () => {
        const sampleSettings = {
          ...DEFAULT_SETTINGS,
          athleteProfile: {
            answers: getDefaultCalibrationAnswers(),
            params: deriveAthleteParams(getDefaultCalibrationAnswers()),
            isCalibrated: true,
          },
        }

        const stats = calculateSegmentStats(
          SAMPLE_GPX_DATA.points,
          0,
          SAMPLE_GPX_DATA.points.length - 1,
        )
        const initialSegment: Segment = {
          id: crypto.randomUUID(),
          name: 'Start to Finish',
          startPointIndex: 0,
          endPointIndex: SAMPLE_GPX_DATA.points.length - 1,
          distance: SAMPLE_GPX_DATA.totalDistance,
          elevationGain: SAMPLE_GPX_DATA.totalElevationGain,
          elevationLoss: SAMPLE_GPX_DATA.totalElevationLoss,
          avgGrade: stats.avgGrade,
          checkpointType: 'None',
          terrainType: 'SmoothTrail',
          stopTime: 0,
        }

        set({
          gpxData: SAMPLE_GPX_DATA,
          segments: [initialSegment],
          settings: sampleSettings,
        })
        get().recalculatePacing()
      },

      addSplitAtKm: (km, splitName) => {
        const { gpxData, segments } = get()
        if (!gpxData) return
        let closestIndex = 0
        let minDiff = Infinity
        gpxData.points.forEach((pt, idx) => {
          const diff = Math.abs((pt.distFromStart || 0) - km)
          if (diff < minDiff) {
            minDiff = diff
            closestIndex = idx
          }
        })
        const segmentIndex = segments.findIndex(
          (s) =>
            s.startPointIndex < closestIndex && s.endPointIndex > closestIndex,
        )
        if (segmentIndex === -1) return

        const targetSegment = segments[segmentIndex]
        const stats1 = calculateSegmentStats(
          gpxData.points,
          targetSegment.startPointIndex,
          closestIndex,
        )
        const stats2 = calculateSegmentStats(
          gpxData.points,
          closestIndex,
          targetSegment.endPointIndex,
        )

        const newSegment1: Segment = {
          ...targetSegment,
          id: targetSegment.id,
          name: splitName || `CP ${km.toFixed(1)}km`,
          endPointIndex: closestIndex,
          distance: stats1.distance,
          elevationGain: stats1.elevationGain,
          elevationLoss: stats1.elevationLoss,
          avgGrade: stats1.avgGrade,
        }
        const newSegment2: Segment = {
          ...targetSegment,
          id: crypto.randomUUID(),
          name: targetSegment.name,
          startPointIndex: closestIndex,
          endPointIndex: targetSegment.endPointIndex,
          distance: stats2.distance,
          elevationGain: stats2.elevationGain,
          elevationLoss: stats2.elevationLoss,
          avgGrade: stats2.avgGrade,
          checkpointType: targetSegment.checkpointType,
          terrainType: targetSegment.terrainType || 'SmoothTrail',
          stopTime: targetSegment.stopTime,
          paceOverride: undefined,
        }

        const newSegments = [...segments]
        newSegments.splice(segmentIndex, 1, newSegment1, newSegment2)
        set({ segments: newSegments })
        get().recalculatePacing()
      },

      updateSegment: (id, updates) => {
        const { settings } = get()
        if (updates.checkpointType && updates.stopTime === undefined) {
          const defaultStop =
            settings.defaultStopTimes?.[updates.checkpointType] ?? 0
          updates = { ...updates, stopTime: defaultStop }
        }
        set((state) => ({
          segments: state.segments.map((s) =>
            s.id === id ? { ...s, ...updates } : s,
          ),
        }))
        get().recalculatePacing()
      },

      removeSegment: (id) => {
        const { segments, gpxData } = get()
        if (!gpxData) return
        if (segments.length <= 1) return // Can't delete the only segment
        const index = segments.findIndex((s) => s.id === id)
        if (index === -1) return

        let mergedSegment: Segment
        let spliceIndex: number

        if (index === 0) {
          // First segment: merge forward into the next segment
          const nextSegment = segments[index + 1]
          const stats = calculateSegmentStats(
            gpxData.points,
            segments[index].startPointIndex,
            nextSegment.endPointIndex,
          )
          mergedSegment = {
            ...nextSegment,
            startPointIndex: segments[index].startPointIndex,
            distance: stats.distance,
            elevationGain: stats.elevationGain,
            elevationLoss: stats.elevationLoss,
            avgGrade: stats.avgGrade,
          }
          spliceIndex = 0
        } else {
          // Non-first segment: merge backward into the previous segment
          const prevSegment = segments[index - 1]
          const targetSegment = segments[index]
          const stats = calculateSegmentStats(
            gpxData.points,
            prevSegment.startPointIndex,
            targetSegment.endPointIndex,
          )
          mergedSegment = {
            ...prevSegment,
            endPointIndex: targetSegment.endPointIndex,
            distance: stats.distance,
            elevationGain: stats.elevationGain,
            elevationLoss: stats.elevationLoss,
            avgGrade: stats.avgGrade,
          }
          spliceIndex = index - 1
        }

        const newSegments = [...segments]
        newSegments.splice(spliceIndex, 2, mergedSegment)
        set({ segments: newSegments })
        get().recalculatePacing()
      },

      updateSettings: (updates) => {
        // When switching FROM custom to a real model, clear all pace overrides
        const currentSettings = get().settings
        const switchingFromCustom =
          currentSettings.activeModelId === 'custom' &&
          updates.activeModelId &&
          updates.activeModelId !== 'custom'

        if (switchingFromCustom) {
          set((state) => ({
            settings: { ...state.settings, ...updates },
            segments: state.segments.map((s) => ({
              ...s,
              paceOverride: undefined,
            })),
          }))
        } else {
          set((state) => ({ settings: { ...state.settings, ...updates } }))
        }
        get().recalculatePacing()
      },

      updateAthleteCalibration: (answers) => {
        const params = deriveAthleteParams(answers)
        set((state) => ({
          settings: {
            ...state.settings,
            athleteProfile: {
              answers,
              params,
              isCalibrated: true,
            },
          },
        }))
        get().recalculatePacing()
      },

      updateNutritionBaselines: (updates) => {
        set((state) => ({
          settings: {
            ...state.settings,
            athleteProfile: {
              ...state.settings.athleteProfile,
              params: {
                ...state.settings.athleteProfile.params,
                ...updates,
              },
            },
          },
        }))
        get().recalculatePacing()
      },

      updateSegmentPace: (segmentId, pace) => {
        const state = get()
        const updates: Partial<RaceSettings> = {}

        // If we're not already in custom mode, switch to it and save previous
        if (state.settings.activeModelId !== 'custom') {
          updates.activeModelId = 'custom'
          updates.previousModelId = state.settings.activeModelId
        }

        set((state) => ({
          settings: { ...state.settings, ...updates },
          segments: state.segments.map((s) =>
            s.id === segmentId ? { ...s, paceOverride: pace } : s,
          ),
        }))
        get().recalculatePacing()
      },

      refreshWeather: async () => {
        const { gpxData, settings } = get()
        if (!gpxData || gpxData.points.length === 0) return

        const startPt = gpxData.points[0]
        const startTime = new Date(settings.startTime)

        const weather = await fetchWeatherForecast(
          startPt.lat,
          startPt.lon,
          startTime,
          24,
        )
        if (weather) {
          set({ weatherForecast: weather })
          get().recalculatePacing()
        }
      },

      recalculatePacing: () => {
        const state = get()
        const { segments, settings, gpxData, weatherForecast } = state

        if (!settings.athleteProfile.isCalibrated) {
          return
        }

        // Handle 'custom' model by falling back to previous or default
        const effectiveModelId =
          settings.activeModelId === 'custom'
            ? settings.previousModelId || 'terrain-v1'
            : settings.activeModelId

        const model = getModelById(effectiveModelId)
        const athleteParams = settings.athleteProfile.params

        let accumulatedMinutes = 0
        let runningMinutes = 0
        const startTime = new Date(settings.startTime)

        const optimalResults: PacingResult[] = segments.map((segment) => {
          const segmentStart = addMinutes(startTime, accumulatedMinutes)
          const climate = getClimateCondition(weatherForecast, segmentStart)
          const climateImpact = calculateClimateImpact(
            climate,
            athleteParams,
            runningMinutes,
          )

          const extraParams = {
            _elapsedMinutes: runningMinutes,
            ...(gpxData ? { _gpxPoints: gpxData.points } : {}),
          }

          let duration: number
          let pace: number

          if (segment.paceOverride && segment.paceOverride > 0) {
            pace = segment.paceOverride
            duration = segment.distance * pace
          } else {
            duration = model.computeSegmentTime(
              segment,
              athleteParams,
              climateImpact,
              extraParams,
            )
            pace = duration / segment.distance
          }

          runningMinutes += duration
          const stopTime = segment.stopTime || 0
          accumulatedMinutes += duration + stopTime
          const eta = addMinutes(startTime, accumulatedMinutes)

          const etaHour = eta.getHours()
          const sunriseHour = parseInt(settings.sunriseTime.split(':')[0])
          const sunsetHour = parseInt(settings.sunsetTime.split(':')[0])
          const isNight = etaHour < sunriseHour || etaHour >= sunsetHour

          return {
            segmentId: segment.id,
            duration,
            stopTime,
            totalTime: duration + stopTime,
            accumulatedTime: accumulatedMinutes,
            eta,
            isNight,
            pace,
            climate,
            climateImpact,
          }
        })

        const finalResults = applyScenario(
          optimalResults,
          segments,
          settings.executionScenario,
          settings,
          gpxData?.totalDistance || 0,
        )

        set({ pacingResults: finalResults })
      },

      addProduct: (product) =>
        set((state) => ({ products: [...state.products, product] })),
      updateProduct: (id, updates) =>
        set((state) => ({
          products: state.products.map((p) =>
            p.id === id ? { ...p, ...updates } : p,
          ),
        })),
      removeProduct: (id) =>
        set((state) => ({
          products: state.products.filter((p) => p.id !== id),
        })),
      updateNutritionPlan: (segmentId, planUpdates) =>
        set((state) => {
          const existing = state.nutritionPlans[segmentId] || {
            segmentId,
            intakeItems: [],
          }
          return {
            nutritionPlans: {
              ...state.nutritionPlans,
              [segmentId]: { ...existing, ...planUpdates },
            },
          }
        }),

      exportProducts: () => {
        return JSON.stringify(get().products, null, 2)
      },

      importProducts: (json: string) => {
        try {
          const imported = JSON.parse(json)
          if (!Array.isArray(imported)) {
            return {
              success: false,
              message: 'Invalid format: expected array',
              count: 0,
            }
          }
          // Basic validation
          const valid = imported.filter((p) => p.id && p.name && p.unitLabel)
          set({ products: valid })
          return {
            success: true,
            message: 'Products imported successfully',
            count: valid.length,
          }
        } catch (e) {
          return { success: false, message: 'Invalid JSON', count: 0 }
        }
      },

      exportRacePlan: () => {
        const state = get()
        const plan = {
          version: 3,
          exportedAt: new Date().toISOString(),
          gpxData: state.gpxData,
          settings: state.settings,
          segments: state.segments,
          products: state.products,
          nutritionPlans: state.nutritionPlans,
        }
        return JSON.stringify(plan, null, 2)
      },

      importRacePlan: (json: string) => {
        try {
          const data = JSON.parse(json)
          if (!data || typeof data !== 'object')
            return { success: false, message: 'Invalid JSON structure' }

          const updates: Partial<RaceState> = {}
          if (data.gpxData) updates.gpxData = data.gpxData

          if (data.settings) {
            updates.settings = { ...get().settings, ...data.settings }
            if (!updates.settings.athleteProfile) {
              updates.settings.athleteProfile = {
                answers: getDefaultCalibrationAnswers(),
                params: deriveAthleteParams(getDefaultCalibrationAnswers()),
                isCalibrated: false,
              }
            }
          }

          if (data.segments) updates.segments = data.segments
          if (data.products) updates.products = data.products
          if (data.nutritionPlans) updates.nutritionPlans = data.nutritionPlans

          set(updates as any)
          get().recalculatePacing()
          return { success: true, message: 'Plan imported successfully' }
        } catch (err) {
          return {
            success: false,
            message: `Parse error: ${(err as Error).message}`,
          }
        }
      },
    }),
    {
      name: 'trail-race-planner-storage',
      merge: (persistedState: any, currentState: any) => {
        const merged = { ...currentState, ...persistedState }

        // Always use the latest DEFAULT_PRODUCTS from code.
        // Only keep persisted products if the user has explicitly customized them
        // (detected by having a different count than any known default size).
        // This ensures new default products are always picked up.
        merged.products = DEFAULT_PRODUCTS

        if (!merged.nutritionPlans) merged.nutritionPlans = {}

        if (merged.settings && !merged.settings.athleteProfile) {
          merged.settings.athleteProfile = {
            answers: getDefaultCalibrationAnswers(),
            params: deriveAthleteParams(getDefaultCalibrationAnswers()),
            isCalibrated: false,
          }
        }
        return merged
      },
    },
  ),
)
